// Reference:
// * https://github.com/epicweb-dev/epic-stack/blob/90ef60d4b2e762a1888fc72abd71f40a5142f978/app/routes/resources+/theme-switch.tsx

import { parseWithZod } from "@conform-to/zod";
import { useFetchers } from "react-router";
import { z } from "zod";

import { useHints } from "@/utils/client-hints";
import { useRequestInfo } from "@/utils/request-info";

import {
  type ColorTheme,
  colorThemeFormSchema,
  DEFAULT_COLOR_THEME,
} from "./color-theme";

export type Theme = "light" | "dark";
export type ThemeMode = Theme | "system";

export const themeFormSchema = z.object({
  theme: z.enum(["light", "dark", "system"]),
  redirectTo: z.string().optional(),
});

// Retrieve the user's current color palette, including an optimistic update
// while a new preference is being saved.
//
export function useColorTheme(): ColorTheme {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const requestInfo = useRequestInfo();
  const optimisticColorTheme = useOptimisticColorTheme();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const preferredColorTheme = requestInfo.userPreference.colorTheme as
    | ColorTheme
    | undefined;

  return optimisticColorTheme ?? preferredColorTheme ?? DEFAULT_COLOR_THEME;
}

// Retrieve the user's current theme preference, or the client hint theme if no
// preference is set.
//
export function useTheme(): Theme {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const hints = useHints();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const requestInfo = useRequestInfo();

  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === "system"
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (hints["theme"] as Theme)
      : optimisticMode;
  }

  /* eslint-disable-next-line @typescript-eslint/no-unsafe-return,
                              @typescript-eslint/no-unsafe-member-access
  */
  return requestInfo.userPreference.theme ?? (hints["theme"] as Theme);
}

// Check if the user is currently changing their color palette preference.
//
export function useOptimisticColorTheme() {
  const fetchers = useFetchers();
  const colorThemeFetcher = fetchers.find(
    (fetcher) => fetcher.formAction === "/color-theme",
  );

  if (colorThemeFetcher?.formData) {
    const submission = parseWithZod(colorThemeFetcher.formData, {
      schema: colorThemeFormSchema,
    });

    if (submission.status === "success") {
      return submission.value.colorTheme;
    }
  }

  return undefined;
}

// Check if the user is currently changing their theme mode preference. Return
// the new theme if a change is in progress.
//
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) => f.formAction === "/theme");

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: themeFormSchema,
    });

    if (submission.status === "success") {
      return submission.value.theme;
    }
  }

  return undefined;
}
